/**
 * @file Selfie Capture Helpers.
 */

import {
  SELFIE_CAPTURE_ERRORS,
  WEB_RTC_ERRORS,
  SELFIE_CAPTURE_ERROR_CODES,
} from './constants';
import {
  getScreenResolution,
  isPortraitResolution,
} from '../../../util/screen';

/**
 * Generates an error instance for Selfie Errors based on the error code.
 *
 * @param {enum<SELFIE_CAPTURE_ERROR_CODES>} errorCode The code to generate the error from.
 */
export const getErrorByCode = errorCode => {
  const error = new Error(SELFIE_CAPTURE_ERRORS[errorCode]);
  error.code = errorCode;

  return error;
};

/**
 * Gets a valid Selfie Capture error based on either an error code or an
 * webRTC error name.
 *
 * @param {String} errorIdentitier Either a valid error code or name.
 */
export const getSelfieCaptureError = errorIdentitier => {
  if (SELFIE_CAPTURE_ERRORS[errorIdentitier]) {
    return getErrorByCode(errorIdentitier);
  }

  if (errorIdentitier === WEB_RTC_ERRORS.NotAllowedError) {
    return getErrorByCode(SELFIE_CAPTURE_ERROR_CODES.SC001);
  }

  if (
    [
      WEB_RTC_ERRORS.NotFoundError,
      WEB_RTC_ERRORS.DevicesNotFoundError,
    ].includes(errorIdentitier)
  ) {
    return getErrorByCode(SELFIE_CAPTURE_ERROR_CODES.SC002);
  }

  // no error found.
  return undefined;
};

/**
 * Given a dataURL, returns a ready to upload file.
 * Check https://stackoverflow.com/questions/6850276/how-to-convert-dataurl-to-file-object-in-javascript.
 *
 * @param {String} dataURI The Data URL to convert to a file.
 * @param {String} fileName The file name.
 */
export const dataURItoFile = (dataURI, fileName) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }

  const file = new File([ab], fileName, {
    type: mimeString,
  });

  return file;
};

// SVG Path have a dimension given by how they were made during the design phase.
// The current path shape in use has the following:
export const FACE_PATH_DIMENSION = {
  height: 500,
  width: 387,
}; // px

/**
 * Returns a configuration object used to display the facebox.
 * The computations here are based on the current screen resolution, rather than
 * the videoStream resolution.
 *
 * @param {Object} resolution The Screen / Video resolution to position the face at.
 */
export const getFaceMaskConfig = (resolution = getScreenResolution()) => {
  const isPortrait = isPortraitResolution(resolution);

  // calculate the transform values:
  const scale =
    (resolution.height / FACE_PATH_DIMENSION.height) *
    (isPortrait ? 0.6 : 0.85);

  const translate = {
    x: Math.round(
      (resolution.width - FACE_PATH_DIMENSION.width * scale) / (2 * scale),
    ),
    y: Math.round(
      (resolution.height - FACE_PATH_DIMENSION.height * scale) / (2 * scale),
    ),
  };

  const viewBox = {
    width: resolution.width,
    height: resolution.height,
  };

  return {
    scale,
    translate,
    viewBox,
  };
};
